// ⚠️ #1522 — the console.warn calls in this file stay warn, deliberately.
//
// main.tsx runs captureConsoleIntegration at levels ["error"], so a warn
// produces no Sentry event. That is the point here: session, timezone and DAU telemetry
// fails on a timer or a shim, nothing she did depends on it, and nothing is
// waiting on the result. The repo's rule is "warn means expected and handled,
// error means a human should see it" — these are the warns that rule exists
// for, and converting them is the quota flood main.tsx's comment warns about.
//
// A failed WRITE she initiated does not belong here. If one is added to this
// file, report it with reportError.

// Lightweight analytics: feature usage, session tracking, and daily active
// user counts. All writes are fire-and-forget. Failures are swallowed —
// analytics must NEVER break the app.

import { supabase } from "@/integrations/supabase/client";
// #355 — this used to be a local copy that read `window.median` as "iOS", so
// every Android app session was written to user_sessions as `ios_app_store`.
// One shared implementation now; see src/lib/platform.ts for the full story.
import { detectPlatform } from "@/lib/platform";

const SESSION_GAP_MS = 30 * 60 * 1000; // 30 min idle = new session

let currentSessionId: string | null = null;
let currentSessionUserId: string | null = null;
let currentSessionStartMs: number | null = null;
let lastHiddenAtMs: number | null = null;
let listenersInstalled = false;

// Cached admin status to skip analytics writes for admin testers.
// Default to false (track) so missing/failed lookups don't drop real user data.
let isAdminCached = false;
let adminCheckedForUserId: string | null = null;

const ensureAdminChecked = async (userId: string): Promise<void> => {
  if (adminCheckedForUserId === userId) return;
  adminCheckedForUserId = userId;
  isAdminCached = false;
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("is_admin")
      .eq("id", userId)
      .maybeSingle();
    if (error) return;
    isAdminCached = !!data?.is_admin;
  } catch {
    // default: track
  }
};

/** Persist the device's IANA timezone to profiles.timezone (fire-and-forget).
 *  This column is the source of truth for the free-tier message cap's day
 *  boundary (see the user_cap_day() SQL helper). Writes go through the
 *  set_user_timezone() RPC — the client cannot UPDATE profiles directly, and a
 *  blanket update policy would let users self-grant is_admin/tier. Only writes
 *  when the stored value differs, so it's not a write on every app open. */
const captureTimezone = async (userId: string): Promise<void> => {
  try {
    let tz = "";
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {
      return; // no Intl / no resolvable zone — leave it for the Eastern fallback
    }
    if (!tz) return;

    const { data, error } = await supabase
      .from("profiles")
      .select("timezone")
      .eq("id", userId)
      .maybeSingle();
    if (error) return;
    if ((data as { timezone?: string | null } | null)?.timezone === tz) return;

    const { error: rpcErr } = await supabase.rpc(
      "set_user_timezone" as never,
      { p_timezone: tz } as never,
    );
    if (rpcErr) console.warn("set_user_timezone failed:", rpcErr.message);
  } catch (e) {
    console.warn("captureTimezone failed:", e);
  }
};

/** Get the active session id (for linking feature_usage rows). */
export const getCurrentSessionId = (): string | null => currentSessionId;

/** Get the current authenticated user id (set by startSessionForUser). */
export const getCurrentUserId = (): string | null => currentSessionUserId;

/** Insert one feature_usage row. Fire-and-forget. */
export const trackFeature = (
  featureName: string,
  metadata?: Record<string, unknown>
): void => {
  void (async () => {
    try {
      const userId = currentSessionUserId;
      if (!userId) return;
      await ensureAdminChecked(userId);
      if (isAdminCached) return;
      await supabase.from("feature_usage").insert([{
        user_id: userId,
        feature_name: featureName,
        metadata: (metadata ?? null) as never,
        session_id: currentSessionId,
      }]);
    } catch (e) {
      console.warn("trackFeature failed:", e);
    }
  })();
};

const endCurrentSession = (useBeacon: boolean) => {
  if (!currentSessionId || !currentSessionStartMs) return;
  const sessionId = currentSessionId;
  const endMs = Date.now();
  const duration = Math.max(0, Math.round((endMs - currentSessionStartMs) / 1000));
  // Reset locals immediately so re-entries don't double-write
  currentSessionId = null;
  currentSessionStartMs = null;

  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_sessions?id=eq.${sessionId}`;
    const body = JSON.stringify({
      session_end: new Date(endMs).toISOString(),
      duration_seconds: duration,
    });
    
    void supabase
      .from("user_sessions")
      .update({
        session_end: new Date(endMs).toISOString(),
        duration_seconds: duration,
      })
      .eq("id", sessionId)
      .then(({ error }) => {
        if (error) console.warn("session end update failed:", error.message);
      });
      
    // Also try a keepalive fetch as a backup on unload paths.
    if (useBeacon && typeof fetch !== "undefined") {
      const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      void supabase.auth.getSession().then(({ data }) => {
        const token = data.session?.access_token;
        if (!token) return;
        try {
          fetch(url, {
            method: "PATCH",
            keepalive: true,
            headers: {
              "Content-Type": "application/json",
              apikey,
              Authorization: `Bearer ${token}`,
              Prefer: "return=minimal",
            },
            body,
          }).catch(() => {});
        } catch {
          // Ignore
        }
      });
    }
  } catch (e) {
    console.warn("endCurrentSession failed:", e);
  }
};

const startNewSession = async (userId: string) => {
  try {
    await ensureAdminChecked(userId);
    if (isAdminCached) return;
    const platform = detectPlatform();
    const { data, error } = await supabase
      .from("user_sessions")
      .insert({ user_id: userId, platform })
      .select("id")
      .single();
    if (error || !data) {
      console.warn("session insert failed:", error?.message);
      return;
    }
    currentSessionId = data.id;
    currentSessionUserId = userId;
    currentSessionStartMs = Date.now();

    // Bump DAU (atomic upsert via RPC) — also captures latest platform
    void supabase
      .rpc("bump_daily_active_session" as never, { _platform: platform } as never)
      .then(({ error: rpcErr }) => {
        if (rpcErr) console.warn("bump_daily_active_session failed:", rpcErr.message);
      });
  } catch (e) {
    console.warn("startNewSession failed:", e);
  }
};

const installVisibilityListeners = () => {
  if (listenersInstalled || typeof window === "undefined") return;
  listenersInstalled = true;

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      lastHiddenAtMs = Date.now();
      endCurrentSession(true);
    } else if (document.visibilityState === "visible") {
      const userId = currentSessionUserId;
      if (!userId) return;
      const gap = lastHiddenAtMs ? Date.now() - lastHiddenAtMs : 0;
      if (gap > SESSION_GAP_MS && !currentSessionId) {
        void startNewSession(userId);
      }
    }
  });

  window.addEventListener("beforeunload", () => {
    endCurrentSession(true);
  });

  window.addEventListener("pagehide", () => {
    endCurrentSession(true);
  });
};

/** Called once auth resolves with a logged-in user. Idempotent per userId. */
export const startSessionForUser = (userId: string) => {
  installVisibilityListeners();
  // Keep the user's timezone current for the message-cap day boundary. Runs for
  // everyone (incl. admins, who bypass the cap) so the column is a true fact.
  void captureTimezone(userId);
  if (currentSessionId && currentSessionUserId === userId) return;
  if (currentSessionUserId && currentSessionUserId !== userId) {
    // User switched — close old session
    endCurrentSession(false);
  }
  currentSessionUserId = userId;
  void startNewSession(userId);
};

/** Called on sign-out. */
export const clearSessionTracking = () => {
  endCurrentSession(false);
  currentSessionUserId = null;
  lastHiddenAtMs = null;
  adminCheckedForUserId = null;
  isAdminCached = false;
};

/** Bump today's DAU message count. Fire-and-forget. */
export const trackMessageForDau = () => {
  void (async () => {
    try {
      const userId = currentSessionUserId;
      if (!userId) return;
      await ensureAdminChecked(userId);
      if (isAdminCached) return;
      const { error } = await supabase.rpc("bump_daily_active_message" as never);
      if (error) console.warn("bump_daily_active_message failed:", error.message);
    } catch (e) {
      console.warn("trackMessageForDau failed:", e);
    }
  })();
};
